import { EditActions, EditActionsEnum } from "../actions/editActions";
import { editInitialState } from "./initialState";
import { isEqual } from "lodash";

const editReducer = (
  state: State.EditView = editInitialState,
  action: EditActions
): State.EditView => {
  switch (action.type) {
    case EditActionsEnum.SET_EDITED_OBJECT: {
      const editedObject = action.payload;
      const hasEdited =
        action.payload === undefined
          ? undefined
          : !isEqual(editedObject, state.storedObject) || state.hasEdited;
      return { ...state, editedObject, hasEdited };
    }
    case EditActionsEnum.SET_STORED_OBJECT: {
      const storedObject = action.payload;
      return { ...state, storedObject };
    }
    case EditActionsEnum.SET_PARENT_OBJECT: {
      const parentObject = action.payload;
      return { ...state, parentObject };
    }
    case EditActionsEnum.SET_EDITED_OBJECT_TYPE: {
      const editedObjectType = action.payload;
      return { ...state, editedObjectType };
    }
    case EditActionsEnum.CLEAR_HAS_EDITED: {
      return { ...state, hasEdited: undefined };
    }
    case EditActionsEnum.PASTE_OBJECT: {
      const clipboard = state.clipboard;
      if (clipboard.option === "standardActions") {
        return {
          ...state,
          editedObject: {
            ...state.editedObject,
            standardActions: [...state.editedObject.standardActions, ...clipboard.data]
          }
        };
      } else if (clipboard.option === "ionodePointActions") {
        return {
          ...state,
          editedObject: {
            ...state.editedObject,
            ionodePointActions: [...state.editedObject.ionodePointActions, ...clipboard.data]
          }
        };
      }
      return { ...state };
    }
    case EditActionsEnum.COPY_OBJECT: {
      const clipboard = action.payload;

      return { ...state, clipboard };
    }
    default:
      return state;
  }
};

export const editedObjectIdSelector = (state: State.EditView): Api.AnyItem =>
  state.editedObject?.id
    ? state.editedObject.id
    : state.editedObject?.enumValue
      ? state.editedObject.enumValue
      : state.editedObject?.boardTypeId
        ? state.editedObject.boardTypeId
        : state.editedObject?.value
          ? state.editedObject.value
          : undefined;

export const parentObjectIdSelector = (state: State.EditView): Api.AnyItem =>
  state.parentObject?.id;

export const isEditActiveSelector = (state: State.EditView): Api.AnyItem => !!state.editedObject;
/**
 * In editedObjectIdentifierSelector we return the enumName/productName rather than enumValue/code
 * so user can easier identity which item she is modifying
 */
export const editedObjectIdentifierSelector = (
  state: State.EditView,
  route: State.AnySubMenu | State.AnyChildItem
): Api.AnyItem => {
  switch (route) {
    case "Products":
      return state.editedObject && (state.editedObject as Api.KceProduct).productName;
    case "Versions":
      return state.editedObject && (state.editedObject as Api.KceVersion).name;
    case "Drive Versions":
      return (
        state.editedObject &&
        (state.editedObject as Api.DriveVersion).year +
          "-" +
          (state.editedObject as Api.DriveVersion).week
      );
    case "Fault Components":
      return state.editedObject && (state.editedObject as Api.FaultComponentEnumeration).enumName;
    case "Domains":
      return state.editedObject && (state.editedObject as Api.Domain).enumName;
    case "Parameter Categories":
      return state.editedObject && (state.editedObject as Api.ParameterCategory).enumName;
    case "Elevator Faults":
    case "APM Faults":
    case "Group Faults":
    case "Network Faults":
    case "Monitoring Faults":
    case "Drive Faults":
    case "NonKone Faults":
    case "Escalator Faults":
    case "Door Faults":
    case "Signalization Faults":
      return state.editedObject && (state.editedObject as Api.BaseFault).name;
    case "Elevator Parameters":
    case "Group Parameters":
    case "Drive Parameters":
    case "APM Parameters":
    case "Elevator Bindings":
    case "Drive Bindings":
    case "Safety Parameters":
      return (
        (state.editedObject && (state.editedObject as Api.BaseParameter).name) ||
        (state.editedObject as Api.KceProduct)?.productName
      );
    case "Escalator Parameters":
      return state.editedObject && (state.editedObject as Api.BaseParameter).name;
    case "Door Parameters":
      return state.editedObject && (state.editedObject as Api.BaseParameter).name;
    case "Drive Signals":
      return state.editedObject && (state.editedObject as Api.DriveSignal).name;
    case "Drive Tests":
      return state.editedObject && (state.editedObject as Api.DriveTest).name;
    case "Drive Signal Groups":
      return state.editedObject && (state.editedObject as Api.DriveSignalGroup).name;
    case "Escalator RTDs":
      return state.editedObject && (state.editedObject as Api.EscalatorRTD).name;
    case "Escalator RTD Groups":
      return state.editedObject && (state.editedObject as Api.EscalatorRTDGroup).name;
    case "Event Logs":
      return state.editedObject && (state.editedObject as Api.EscalatorEventLog).name;
    case "Data Act":
      return state.editedObject && (state.editedObject as Api.DataActCode).name;
    case "Users":
      return state.editedObject && (state.editedObject as Api.User).adAccount;
    case "Event Classes":
    case "Event Types":
    case "Event Pids":
    case "Electrifications":
    case "Ext. Commands":
    case "Event Enumerations":
    case "IO Enumerations":
    case "IO Networks":
    case "IO CPUs":
    case "Message Types":
      return state.editedObject && (state.editedObject as Api.BaseEnumeration).enumName;
    case "Door Event Types":
      return state.editedObject && (state.editedObject as Api.EventTypesEnum).enumName;
    case "Escalator Event Types":
      return state.editedObject && (state.editedObject as Api.EventEnumeration).enumName;
    case "Door Event Classes":
    case "Door Event Pids":
    case "Door Ext. Commands":
    case "Escalator Event Classes":
    case "Escalator Event Pids":
    case "Escalator Ext. Commands":
    case "Escalator Request Types":
      return state.editedObject && (state.editedObject as Api.EventEnumeration).enumName;
    case "Escalator Versions": //as of now not used
      return state.editedObject && (state.editedObject as Api.EscalatorVersion).versionName;
    case "Escalator Bindings":
      return state.editedObject && (state.editedObject as Api.EscalatorVersion).versionName;
    case "IO Basic Data Types":
      return state.editedObject && (state.editedObject as Api.IoBasicDataType).enumName;
    case "IO Data Sections":
      return state.editedObject && (state.editedObject as Api.IodataSections).name;
    case "IO Board Types":
      return state.editedObject && (state.editedObject as Api.IoBoardType).enumName;
    case "IO Datatypes":
    case "IO Options":
      return state.editedObject && (state.editedObject as Api.ioOption).enumName;
    case "Standard Boards":
      return state.editedObject && (state.editedObject as Api.StandardBoard).revision;
    case "IO configuration":
      return state.editedObject && (state.editedObject as Api.IoBoard).standard?.revision;
    case "AADGroups":
      return state.editedObject && (state.editedObject as Api.AADGroup).name;
    case "Common Enumerations":
      return state.editedObject && (state.editedObject as Api.CommonEnumeration).name;
    case "Languages":
      return state.editedObject && (state.editedObject as Api.Locale).locale;
    case "IO Options Enum Type":
      return state.editedObject && (state.editedObject as Api.IoOptionsEnumType).enumTypeName;
    case "Status":
    case "Translation":
    case "Translation Log":
    case "Publish":
    case "Development Package":
    case "License Data":
    case "Add Bindings":
    case "Remove Bindings":
    case "Door Bindings":
      return "N/A";
    case "Door Versions":
      return state.editedObject && (state.editedObject as Api.DoorVersion).versionName;
    case "Monitoring Fault Categories":
      return state.editedObject && (state.editedObject as Api.FaultCategory).name;
    case "Data Acts":
      return state.editedObject && (state.editedObject as Api.EscalatorDataActCode).name;
    case "Grand Norm":
      return state.editedObject && (state.editedObject as Api.GrandNorm).name;
    default:
      console.error("Unexpected error in editedObjectIdentifier");
  }
};
export const domainSelector = (state: State.EditView): Api.AnyItem =>
  state.editedObject &&
  state.editedObject.commonExtra &&
  (state.editedObject as Api.AnyFault).commonExtra?.domainName;

export default editReducer;
